import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useTranslation } from '@/hooks/useTranslation';
import { useModalHelper } from '@/hooks/useModalHelper';
import useRequireAuth from '@/hooks/useRequireAuth';

import MetaHead from '@/components/MetaHead';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import StatusContainer from '@/components/StatusContainer';
import ToolPageCopy from '@/components/Tools/ToolPageCopy';
import TasksIntro from '@/components/Tasks/TasksIntro';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';
import { CATALOG_VIEW_MODES } from '@/components/Catalog/CatalogBrowseShell';
import TasksWorkspaceToolbar from '@/components/Tasks/TasksWorkspaceToolbar';
import TasksEmptyState from '@/components/Placeholders/TasksEmptyState';
import TasksFavoritesEmptyState from '@/components/Placeholders/TasksFavoritesEmptyState';
import TaskCardCalendar, { TaskCardCalendarPlaceholder } from '@/components/Tasks/TaskCardCalendar';
import TemplatePinCard, { TemplatePinCardPlaceholder } from '@/components/Tasks/TemplatePinCard';
import TemplatePinListItem, { TemplatePinListItemPlaceholder } from '@/components/Tasks/TemplatePinListItem';
import CatalogCategoryDropdown from '@/components/Dropdowns/CatalogCategoryDropdown';
import CatalogSortDropdown from '@/components/Catalog/CatalogSortDropdown';

import {
  useAuthStore,
  useInferenceTasksStore,
  useModalStore,
  useTaskTemplatesStore,
  useToastStore,
} from '@/store';

import { tasksDiscoveryHeadline, tasksDiscoverySubtitle } from '@/utils/tasks_catalog';
import { normalizeCategoryPageCopy, buildCategoryFaqBlock } from '@/utils/pageCopyBlocks';

const VIEW_MODES = CATALOG_VIEW_MODES;

// ─── Page ──────────────────────────────────────────────────────────────────

// `isXS` ⊂ `isSM` ⊂ `isMD` (each ≤-bound), so style decisions here read
// most-narrow-first to keep the cascade unambiguous.
const createStyles = (colors, isXS, isSM, isMD, isXL, insetBottom = 0) => {
  const gridGap = isXS ? 10 : isSM ? 10 : 12;

  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.solid_color_primary,
      borderTopWidth: Platform.OS === 'web' ? 1 : 0,
      borderTopColor: colors.border_color_secondary,
    },
    scrollContent: {
      paddingBottom: insetBottom + 60,
    },
    contentWrap: {
      paddingHorizontal: 0,
      maxWidth: 1240,
      width: '100%',
      alignSelf: 'center',
    },
    section: {
      paddingHorizontal: isXS ? 16 : 24,
      paddingTop: 8,
      gap: 24,
    },
    pageCopyWrap: {
      marginTop: 8,
      paddingTop: 20,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: colors.border_color_secondary,
      position: 'relative',
      zIndex: 1,
      width: '100%',
    },
    categoryMetaIntro: {
      marginTop: 20,
      paddingTop: 20,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: colors.border_color_secondary,
      gap: 8,
      position: 'relative',
      zIndex: 1,
      width: '100%',
    },
    templateListStack: {
      width: '100%',
      gap: 8,
    },
    block: { gap: 12 },
    blockHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    cardGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: gridGap,
      width: '100%',
    },
    cardGridFlow: {
      alignItems: 'flex-start',
    },
    pinCell: {
      minWidth: 0,
      flexShrink: 0,
      alignSelf: 'stretch',
      ...(Platform.OS === 'web' ? { display: 'flex', flexDirection: 'column' } : {}),
    },
    empty: {
      width: '100%',
      padding: 20,
      borderRadius: 12,
      backgroundColor: colors.solid_color_block,
      alignItems: 'center',
      gap: 12,
    },
  });
};

// `categorySlug` mirrors the legacy contract: the path-based route
// `/tasks/category/[slug]` renders this same page with the prop set,
// which always wins over a stale `?category=` query string.
// `browseMode` — `/tasks/browse` reuses this layout; category URLs and
// template detail navigation stay shared with `/tasks`.
export const TasksPage = React.memo(({ categorySlug: categorySlugProp = null, browseMode = false } = {}) => {
  const { colors, theme } = useTheme();
  const { t } = useTranslation();
  const router = useRouter();
  const params = useLocalSearchParams();
  const { isXS, isSM, isMD, isXL } = useResponsiveness();
  const insets = useSafeAreaInsets();

  const userId = useAuthStore(s => s.user?.id);
  const hasUser = Boolean(userId);

  const [refreshing, setRefreshing] = useState(false);
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
  // "My Tasks" rail scope: when on, the page hides the template catalog
  // (category copy, etc.) and only renders the user's own tasks
  // so they have a fast way to jump straight to what they already created.
  const [showOnlyMyTasks, setShowOnlyMyTasks] = useState(hasUser && !browseMode && !categorySlugProp && !params?.category);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState(VIEW_MODES.LIST);
  const [catalogSort, setCatalogSort] = useState('relevant');
  const [templateLimit, setTemplateLimit] = useState(12);
  const palette = getCatalogPalette(theme);

  const scrollRef = useRef(null);
  const scrollYRef = useRef(0);

  const favoriteTaskIds = useAuthStore(s => s.user?.favoriteTaskIds);
  const favoriteTaskTemplateIds = useAuthStore(s => s.user?.favoriteTaskTemplateIds);
  const { openModal } = useModalStore(s => s.actions);
  const requireAuth = useRequireAuth();
  const { addToast } = useToastStore(s => s.actions);
  const { useConfirmationModal } = useModalHelper();
  const showConfirmationModal = useConfirmationModal();

  // Tasks store
  const tasksMap = useInferenceTasksStore(s => s.tasks);
  const taskIds = useInferenceTasksStore(s => s.taskIds);
  const isLoadingTasks = useInferenceTasksStore(s => s.isLoading.fetch);
  const taskErrors = useInferenceTasksStore(s => s.errors);
  const fetchTasks = useInferenceTasksStore(s => s.fetchTasks);
  const fetchTask = useInferenceTasksStore(s => s.fetchTask);
  const getTaskById = useInferenceTasksStore(s => s.getTaskById);
  const clearTasks = useInferenceTasksStore(s => s.clearTasks);
  const deleteTask = useInferenceTasksStore(s => s.deleteTask);

  // Templates store
  const templates = useTaskTemplatesStore(s => s.templates);
  const categories = useTaskTemplatesStore(s => s.categories);
  const isLoadingTemplates = useTaskTemplatesStore(s => s.isLoading);
  const isFetchedTemplates = useTaskTemplatesStore(s => s.isFetched);
  const templatesFetchError = useTaskTemplatesStore(s => s.error);
  const fetchTemplates = useTaskTemplatesStore(s => s.fetchTemplates);
  const setSelectedCategory = useTaskTemplatesStore(s => s.setSelectedCategory);
  const getTemplatesByCategory = useTaskTemplatesStore(s => s.getTemplatesByCategory);

  // Honor `?id=` deep links by opening the matching task once tasks load.
  const handledInitialTaskIdRef = useRef(false);
  const initialTaskId = useMemo(() => {
    if (!params.id) return null;
    return Array.isArray(params.id) ? params.id[0] : params.id;
  }, [params.id]);

  // Resolve the active category from the path prop first, then the
  // legacy `?category=` query. Either form keeps working unchanged.
  const urlCategory = useMemo(() => {
    const raw = params?.category;
    if (Array.isArray(raw)) return raw[0] || null;
    return typeof raw === 'string' && raw ? raw : null;
  }, [params?.category]);
  const rawEffectiveCategory = categorySlugProp || urlCategory || null;

  const resolveCategorySlug = useCallback((value) => {
    if (!value) return null;
    if (value === 'featured') return 'featured';
    const wanted = String(value);
    const match = categories.find(
      (c) => c?.slug === wanted || c?.id === wanted || String(c?._id) === wanted,
    );
    return match?.slug || wanted;
  }, [categories]);

  const effectiveCategory = useMemo(
    () => resolveCategorySlug(rawEffectiveCategory),
    [rawEffectiveCategory, resolveCategorySlug],
  );

  useEffect(() => setTemplateLimit(12), [effectiveCategory, searchQuery, showOnlyFavorites, catalogSort]);

  const activeCategoryRecord = useMemo(
    () => (effectiveCategory && effectiveCategory !== 'featured'
      ? categories.find((c) => c.slug === effectiveCategory)
      : null),
    [categories, effectiveCategory],
  );

  const categoryPageCopyBlocks = useMemo(() => {
    if (!activeCategoryRecord) return [];
    const blocks = normalizeCategoryPageCopy(activeCategoryRecord?.meta?.pageCopy);
    const hasFaqBlock = blocks.some((b) => b?.type === 'faq');
    if (hasFaqBlock) return blocks;
    const faqTitle = activeCategoryRecord?.name
      ? `${activeCategoryRecord.name} FAQ`
      : 'Frequently asked questions';
    const faqBlock = buildCategoryFaqBlock(activeCategoryRecord?.meta?.faq, faqTitle);
    return faqBlock ? [...blocks, faqBlock] : blocks;
  }, [activeCategoryRecord]);

  const categoryPageCopyPalette = useMemo(() => {
    const palette = getCatalogPalette(theme);
    return { fg: palette.icon, bg: palette.preview, soft: palette.surface };
  }, [theme]);

  const discoveryHeadline = useMemo(
    () => activeCategoryRecord ? tasksDiscoveryHeadline(activeCategoryRecord, t) : t('navigation.tasks'),
    [activeCategoryRecord, t],
  );
  const discoverySubtitle = useMemo(
    () => activeCategoryRecord ? tasksDiscoverySubtitle(activeCategoryRecord, t) : t('pages.tasks.introLead'),
    [activeCategoryRecord, t],
  );

  // Long-form category copy (model `description`). When `meta.description`
  // is also set, the hero subtitle prefers the meta line and this block shows
  // the editorial body only—so we do not print the same paragraph twice.
  const categoryMetaIntroText = useMemo(() => {
    const d = activeCategoryRecord?.description?.trim();
    if (!d) return '';
    const md = activeCategoryRecord?.meta?.description?.trim();
    if (!md || d === md) return '';
    return d;
  }, [activeCategoryRecord]);

  // When the active category scope changes, jump the page scroller back to
  // the top so template cards are visible — not buried
  // under long category copy the user may have scrolled to on a prior visit.
  const catalogScopeKey = `${categorySlugProp || ''}|${urlCategory || ''}|${showOnlyMyTasks}|${showOnlyFavorites}`;
  const scrollCatalogToTop = useCallback((animated = false) => {
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo?.({ y: 0, animated });
      scrollYRef.current = 0;
    });
  }, []);

  useLayoutEffect(() => {
    scrollCatalogToTop(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset scroll on scope change only
  }, [catalogScopeKey]);

  // Mirror the URL into the store so derived selectors stay correct.
  useEffect(() => {
    setSelectedCategory(effectiveCategory);
  }, [effectiveCategory, setSelectedCategory]);

  // ── Derived data ─────────────────────────────────────────────────────────

  const tasks = useMemo(
    () => taskIds.map(id => tasksMap[id]).filter(Boolean),
    [tasksMap, taskIds]
  );

  const favoriteTaskIdSet = useMemo(
    () => new Set((favoriteTaskIds || []).map(String)),
    [favoriteTaskIds]
  );

  const favoriteTaskTemplateIdSet = useMemo(
    () => new Set((favoriteTaskTemplateIds || []).map(String)),
    [favoriteTaskTemplateIds]
  );

  const visibleTasks = useMemo(() => tasks.filter(task => (
    (!showOnlyFavorites || favoriteTaskIdSet.has(String(task.id)))
    && (!searchQuery.trim() || [task.name, task.description].filter(Boolean).join(' ').toLowerCase().includes(searchQuery.trim().toLowerCase()))
  )), [tasks, showOnlyFavorites, favoriteTaskIdSet, searchQuery]);

  const featuredTemplates = useMemo(
    () => templates.filter(template => template.flags?.isFeatured),
    [templates]
  );

  const filteredTemplates = useMemo(() => {
    if (effectiveCategory === 'featured') return featuredTemplates;
    if (!effectiveCategory) return templates;
    return getTemplatesByCategory(effectiveCategory);
  }, [effectiveCategory, featuredTemplates, getTemplatesByCategory, templates]);

  const visibleCatalogTemplates = useMemo(() => {
    return (showOnlyFavorites ? templates : filteredTemplates).filter(tpl => (!showOnlyFavorites || favoriteTaskTemplateIdSet.has(String(tpl.id)))
      && (!searchQuery.trim() || [tpl.name, tpl.description].filter(Boolean).join(' ').toLowerCase().includes(searchQuery.trim().toLowerCase())));
  }, [templates, filteredTemplates, showOnlyFavorites, favoriteTaskTemplateIdSet, searchQuery]);

  const favoritesCatalogReady = !isLoadingTasks && !(isLoadingTemplates && !isFetchedTemplates);
  const hasAnyFavoriteTasks = favoriteTaskIdSet.size > 0;
  const hasAnyFavoriteTemplates = favoriteTaskTemplateIdSet.size > 0;
  const showFavoritesUnifiedEmpty = showOnlyFavorites
    && favoritesCatalogReady
    && !hasAnyFavoriteTasks
    && !hasAnyFavoriteTemplates;
  const showFavoritesCategoryEmpty = showOnlyFavorites
    && favoritesCatalogReady
    && !showFavoritesUnifiedEmpty
    && (hasAnyFavoriteTasks || hasAnyFavoriteTemplates)
    && visibleTasks.length === 0
    && visibleCatalogTemplates.length === 0;

  const templateOrderIndex = useMemo(() => {
    const m = new Map();
    (templates || []).forEach((tpl, i) => {
      const k = tpl?.id != null ? String(tpl.id) : tpl?.slug;
      if (k) m.set(k, i);
    });
    return m;
  }, [templates]);

  const sortedCatalogTemplates = useMemo(() => {
    const list = [...visibleCatalogTemplates];
    const idx = (tpl) => {
      const k = tpl?.id != null ? String(tpl.id) : tpl?.slug;
      if (k && templateOrderIndex.has(k)) return templateOrderIndex.get(k);
      return Number.MAX_SAFE_INTEGER;
    };
    const usageCount = (tpl) => Number(tpl?.stats?.usageCount) || 0;
    const sortOrderVal = (tpl) => Number(tpl?.sortOrder) || 0;

    switch (catalogSort) {
      case 'relevant':
        return list.sort((a, b) => {
          const ia = idx(a);
          const ib = idx(b);
          if (ia !== ib) return ia - ib;
          const so = sortOrderVal(a) - sortOrderVal(b);
          if (so !== 0) return so;
          return String(a.slug || '').localeCompare(String(b.slug || ''));
        });
      case 'popular':
        return list.sort((a, b) => {
          const ua = usageCount(a);
          const ub = usageCount(b);
          if (ub !== ua) return ub - ua;
          const ia = idx(a);
          const ib = idx(b);
          if (ia !== ib) return ia - ib;
          return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
        });
      case 'name-asc':
        return list.sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }),
        );
      case 'name-desc':
        return list.sort((a, b) =>
          (b.name || '').localeCompare(a.name || '', undefined, { sensitivity: 'base' }),
        );
      case 'category': {
        const catSortKey = (tpl) => {
          if (typeof tpl.category === 'object' && tpl.category?.name) {
            return tpl.category.name;
          }
          const slug = typeof tpl.category === 'string' ? tpl.category : tpl.category?.slug;
          if (slug) {
            const cat = categories.find((c) => c.slug === slug);
            return cat?.name || slug;
          }
          return '\uffff';
        };
        return list.sort((a, b) => {
          const ca = catSortKey(a);
          const cb = catSortKey(b);
          const byCat = ca.localeCompare(cb, undefined, { sensitivity: 'base' });
          if (byCat !== 0) return byCat;
          return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
        });
      }
      default:
        return list;
    }
  }, [visibleCatalogTemplates, catalogSort, templateOrderIndex, categories]);

  // ── Effects ──────────────────────────────────────────────────────────────

  useEffect(() => {
    // Clear before loading the next account, never after another route has loaded its tasks.
    clearTasks();
    // Inference tasks are per-user — skip the fetch (and the deep-link
    // `CreateTaskModal` bootstrap, which would 404 against the user's
    // empty task list) for anonymous visitors. They still see the
    // template catalog and category copy below.
    if (!hasUser) {
      return undefined;
    }
    fetchTasks(1).then(async () => {
      if (!initialTaskId || handledInitialTaskIdRef.current) return;

      let taskToOpen = getTaskById(initialTaskId);
      if (!taskToOpen) {
        try {
          taskToOpen = await fetchTask(initialTaskId);
        } catch (error) {
          console.error('Failed to fetch deep-linked task:', error);
          addToast(t('tasks.taskNotFound'), 'error');
          return;
        }
      }

      if (taskToOpen) {
        handledInitialTaskIdRef.current = true;
        openModal('CreateTaskModal', { task: taskToOpen });
      }
    }).catch((error) => {
      console.error('Failed to fetch tasks:', error);
      addToast(t('pages.tasks.loadFailed'), 'error');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (!isFetchedTemplates) {
      fetchTemplates().catch((error) => {
        console.error('Failed to fetch templates:', error);
        addToast(t('pages.tasks.templateLoadFailed'), 'error');
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetchedTemplates]);

  // ── Callbacks ────────────────────────────────────────────────────────────

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        hasUser ? fetchTasks(1) : Promise.resolve(),
        fetchTemplates(),
      ]);
    } finally {
      setRefreshing(false);
    }
  }, [fetchTasks, fetchTemplates, hasUser]);

  const handleTaskPress = useCallback((task) => {
    openModal('CreateTaskModal', {
      task,
      onSuccess: (result) => {
        if (result?.action === 'delete' && result?.taskId) {
          showConfirmationModal({
            title: t('pages.tasks.deleteTitle'),
            message: t('pages.tasks.deleteMessage'),
            confirmText: t('common.delete'),
            isDangerous: true,
            onConfirm: async () => {
              try {
                await deleteTask(result.taskId);
                addToast(t('pages.tasks.deleteSuccess'), 'success');
              } catch (error) {
                console.error('Failed to delete task:', error);
                addToast(t('pages.tasks.deleteFailed'), 'error');
              }
            },
          });
        }
      },
    });
  }, [openModal, showConfirmationModal, deleteTask, addToast, t]);

  const tasksListBasePath = '/tasks/browse';

  const handleTemplateOpenDetail = useCallback((template) => {
    const slug = template?.slug;
    if (!slug) return;
    router.push(`/tasks/template/${encodeURIComponent(slug)}`);
  }, [router]);

  const handleTemplateScheduleFromCatalog = useCallback((template) => {
    requireAuth(
      () => openModal('SetupTaskFromTemplateModal', { template }),
      {
        source: 'tasks_setup_template',
        title: `Sign in to set up ${template?.name || 'this task'}`,
        subtitle: 'Scheduled tasks run on your account so we know when to start them and where to send the output.',
        icon: 'calendar-clock',
      }
    );
  }, [openModal, requireAuth]);

  const handleCreateCustomTask = useCallback(() => {
    requireAuth(
      () => openModal('CreateTaskModal'),
      {
        source: 'tasks_create_custom',
        title: 'Sign in to create a custom task',
        subtitle: 'Tasks live in your account so they keep running on the schedule you set.',
        icon: 'calendar-plus',
      }
    );
  }, [openModal, requireAuth]);

  const handleToggleFavoritesFilter = useCallback(() => {
    if (!showOnlyFavorites) {
      const ran = requireAuth(
        () => {
          setShowOnlyFavorites(true);
          setShowOnlyMyTasks(false);
        },
        {
          source: 'tasks_favorites_filter',
          title: 'Sign in to see your favorite tasks',
          subtitle: 'Favorites stay tied to your account so they follow you across devices.',
          icon: 'heart',
        }
      );
      if (ran === undefined) return;
      return;
    }
    setShowOnlyFavorites(false);
    setShowOnlyMyTasks(false);
  }, [showOnlyFavorites, requireAuth]);

  const handleToggleMyTasks = useCallback(() => {
    if (!showOnlyMyTasks) {
      const ran = requireAuth(
        () => {
          setShowOnlyMyTasks(true);
          // Activating My Tasks → exit any catalog-side filters and bounce
          // back to the main /tasks URL so category routes don't leave the
          // rail in an inconsistent "category active but catalog hidden" state.
          setShowOnlyFavorites(false);
          if (categorySlugProp || urlCategory || browseMode) {
            router.push('/tasks');
          }
        },
        {
          source: 'tasks_my_tasks_filter',
          title: 'Sign in to see your tasks',
          subtitle: 'Your scheduled tasks live on your account so we know when to run them.',
          icon: 'calendar-check',
        }
      );
      if (ran === undefined) return;
      return;
    }
    setShowOnlyMyTasks(false);
  }, [showOnlyMyTasks, requireAuth, categorySlugProp, urlCategory, browseMode, router]);

  // Push the URL alongside the store update so each scope is shareable
  // and SEO-friendly via the path-based category routes.
  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
  }, []);

  const handleCategoryChange = useCallback((slugInput) => {
    scrollCatalogToTop(false);
    // Picking any catalog scope (All / Featured / a specific category) puts
    // the user back into "browsing templates" mode, so the My Tasks scope
    // shouldn't survive that click.
    setShowOnlyMyTasks(false);
    const slug = resolveCategorySlug(slugInput);

    // Toggle off: clicking the already-active category (or Featured) again
    // clears back to All — no separate "Clear" control needed.
    if (slug && slug === effectiveCategory && !showOnlyFavorites) {
      setSelectedCategory(null);
      router.push(tasksListBasePath);
      return;
    }

    setSelectedCategory(slug);
    if (!slug) {
      router.push(tasksListBasePath);
    } else if (slug === 'featured') {
      router.push(`${tasksListBasePath}?category=featured`);
    } else {
      router.push(`/tasks/category/${encodeURIComponent(slug)}`);
    }
  }, [router, setSelectedCategory, resolveCategorySlug, tasksListBasePath, effectiveCategory, showOnlyFavorites, scrollCatalogToTop]);

  // ── Layout helpers ───────────────────────────────────────────────────────

  // The rail eats 200–280px on the left depending on viewport, so the
  // body gets fewer columns at the same viewport than a full-width grid.
  // Most-narrow-first ordering matches the inclusive ≤-bound ranges.
  const taskCols = isXS ? 1 : isSM ? 1 : isMD ? 2 : isXL ? 3 : 2;
  const pinCols = isXS ? 1 : isSM ? 1 : isMD ? 2 : isXL ? 3 : 2;
  const gridGapPx = isXS ? 10 : isSM ? 10 : 12;
  const gridColumnWidth = (cols) => {
    if (cols <= 1) return '100%';
    if (Platform.OS === 'web') {
      return `calc(${100 / cols}% - ${(gridGapPx * (cols - 1)) / cols}px)`;
    }
    if (cols === 2) return '49%';
    if (cols >= 3) return '32%';
    return `${100 / cols}%`;
  };
  const taskWidth = gridColumnWidth(taskCols);
  const pinWidth = gridColumnWidth(pinCols);
  const pinStretchHeight = pinCols > 1;

  const styles = useMemo(
    () => createStyles(colors, isXS, isSM, isMD, isXL, insets.bottom),
    [colors, isXS, isSM, isMD, isXL, insets.bottom]
  );

  const railCategories = useMemo(
    () => (categories || []).filter(c => c?.slug),
    [categories]
  );

  const scopes = useMemo(() => ([
    { id: '__all', label: t('pages.tasks.scopeAll') || 'All', icon: 'grid-2', value: null },
    { id: '__featured', label: t('pages.tasks.scopeFeatured') || 'Featured', icon: 'star', value: 'featured' },
  ]), [t]);

  const activeKey =
    effectiveCategory == null ? '__all'
      : effectiveCategory === 'featured' ? '__featured'
        : effectiveCategory;

  // Rail/pill items intentionally omit `count` — totals add noise without
  // helping users decide where to click, and dropping the badge column lets
  // the rail run narrower (more room for the catalog grid on the right).
  //
  // Active-state rule: a catalog scope (All / Featured / a category) only
  // reads as "active" when neither overlay filter (`showOnlyFavorites`,
  // `showOnlyMyTasks`) is on — both of those replace the catalog view, so
  // letting an underlying scope still glow as selected would be misleading.
  const tasksCategoryTopItems = useMemo(() => [
    {
      key: 'all',
      label: scopes[0].label,
      icon: scopes[0].icon,
      tint: '#3B82F6',
      isActive: activeKey === scopes[0].id && !showOnlyFavorites && !showOnlyMyTasks,
      onSelect: () => handleCategoryChange(scopes[0].value),
    },
    {
      key: 'featured',
      label: scopes[1].label,
      icon: scopes[1].icon,
      tint: '#F59E0B',
      isActive: activeKey === scopes[1].id && !showOnlyFavorites && !showOnlyMyTasks,
      onSelect: () => handleCategoryChange(scopes[1].value),
    },
  ], [scopes, activeKey, showOnlyFavorites, showOnlyMyTasks, handleCategoryChange]);

  const tasksActiveCategorySlug = useMemo(() => {
    if (showOnlyFavorites || showOnlyMyTasks) return null;
    if (effectiveCategory === 'featured') return null;
    return effectiveCategory;
  }, [showOnlyFavorites, showOnlyMyTasks, effectiveCategory]);

  const taskCatalogSortOptions = useMemo(() => [
    { id: 'relevant', label: t('pages.tasks.catalogSortRelevant') || 'Recommended', icon: 'bars-staggered', iosIcon: 'line.3.horizontal.decrease' },
    { id: 'popular', label: t('pages.tasks.catalogSortPopular') || 'Popular', icon: 'fire', iosIcon: 'flame' },
    { id: 'name-asc', label: t('pages.tasks.catalogSortNameAsc') || 'Name A–Z', icon: 'arrow-down-a-z', iosIcon: 'textformat.abc' },
    { id: 'name-desc', label: t('pages.tasks.catalogSortNameDesc') || 'Name Z–A', icon: 'arrow-up-z-a', iosIcon: 'textformat.abc' },
    { id: 'category', label: t('pages.tasks.catalogSortCategory') || 'By category', icon: 'layer-group', iosIcon: 'square.stack.3d.up' },
  ], [t]);

  const workspaceMode = showOnlyFavorites ? 'saved' : (showOnlyMyTasks && hasUser ? 'tasks' : 'templates');
  const handleWorkspaceMode = (mode) => {
    if (mode === workspaceMode) return;
    setSearchQuery('');
    if (mode === 'tasks') handleToggleMyTasks();
    else if (mode === 'saved') handleToggleFavoritesFilter();
    else {
      setShowOnlyMyTasks(false);
      setShowOnlyFavorites(false);
    }
  };

  // ── Sub-renderers ────────────────────────────────────────────────────────

  const renderMyTasks = () => {
    if (showFavoritesUnifiedEmpty || showFavoritesCategoryEmpty) return null;
    if (showOnlyFavorites && visibleTasks.length === 0) return null;

    if (isLoadingTasks && !tasks.length) {
      return (
        <View style={styles.block}>
          <View style={styles.blockHeader}>
            <Text weight="bold" size="m">{t('pages.tasks.myTasks')}</Text>
          </View>
          <View style={styles.cardGrid}>
            {Array.from({ length: 2 }).map((_, i) => (
              <View key={i} style={{ width: viewMode === VIEW_MODES.LIST ? '100%' : taskWidth, minWidth: 0 }}>
                <TaskCardCalendarPlaceholder layout={viewMode} />
              </View>
            ))}
          </View>
        </View>
      );
    }

    if (taskErrors.fetch && !tasks.length) {
      return (
        <View style={styles.block}>
          <View style={styles.blockHeader}>
            <Text weight="bold" size="m">{t('pages.tasks.myTasks')}</Text>
          </View>
          <StatusContainer
            type="error"
            message={t('pages.tasks.errorLoading').replace('%ERROR%', taskErrors.fetch)}
            actionText={t('pages.common.retry')}
            onAction={onRefresh}
          />
        </View>
      );
    }

    // When the rail "My Tasks" scope is active we own the entire page body,
    // so an empty state must always render — otherwise the user sees nothing.
    if (tasks.length === 0) {
      if (workspaceMode !== 'tasks') return null;
      return (
        <View style={styles.block}>
          <TasksEmptyState onBrowseTemplates={() => handleCategoryChange(null)} />
        </View>
      );
    }

    return (
      <View style={styles.block}>
        <View style={styles.blockHeader}>
          <Text weight="semibold" size="m">{t('pages.tasks.scheduledTasks')}</Text>
          <Text size="xs" color="secondary">{visibleTasks.length}</Text>
        </View>
        <View style={styles.cardGrid}>
          {!visibleTasks.length && <Text size="s" color="secondary">{t('pages.tasks.noSearchResults')}</Text>}
          {visibleTasks.map(task => (
            <View key={task.id} style={{ width: viewMode === VIEW_MODES.LIST ? '100%' : taskWidth, minWidth: 0 }}>
              <TaskCardCalendar task={task} onPress={handleTaskPress} showActions layout={viewMode} />
            </View>
          ))}
        </View>
      </View>
    );
  };

  const renderCatalog = () => {
    if (showOnlyFavorites && visibleTasks.length > 0 && !sortedCatalogTemplates.length && favoritesCatalogReady) return null;
    if (showFavoritesUnifiedEmpty) {
      return (
        <View style={styles.block}>
          <TasksFavoritesEmptyState
            variant="none"
            onBrowseTemplates={handleToggleFavoritesFilter}
          />
        </View>
      );
    }
    if (showFavoritesCategoryEmpty && !searchQuery.trim()) {
      return (
        <View style={styles.block}>
          <TasksFavoritesEmptyState
            variant="category"
            onBrowseTemplates={() => handleCategoryChange(null)}
          />
        </View>
      );
    }

    const headerLabel = showOnlyFavorites ? t('pages.tasks.templates') : effectiveCategory === 'featured'
      ? (t('pages.tasks.featuredTemplates') || 'Featured templates')
      : effectiveCategory
        ? (categories.find(c => c.slug === effectiveCategory)?.name || t('pages.tasks.templates') || 'Templates')
        : (t('pages.tasks.allTemplates') || 'All templates');

    const isCatalogSkeleton = isLoadingTemplates && !isFetchedTemplates;
    const catalogFetchFailed = Boolean(
      templatesFetchError
      && !templates.length
      && !isLoadingTemplates,
    );

    let catalogBody;
    if (catalogFetchFailed) {
      catalogBody = (
        <StatusContainer
          type="error"
          message={templatesFetchError}
          actionText={t('pages.common.retry') || 'Retry'}
          onAction={() => fetchTemplates().catch(() => {})}
        />
      );
    } else if (isCatalogSkeleton) {
      catalogBody = viewMode === VIEW_MODES.LIST ? (
        <View style={styles.templateListStack}>
          {[...Array(isXS ? 5 : 8)].map((_, i) => (
            <TemplatePinListItemPlaceholder key={i} />
          ))}
        </View>
      ) : (
        <View style={[styles.cardGrid, !pinStretchHeight && styles.cardGridFlow]}>
          {Array.from({ length: 6 }).map((_, i) => (
            <View
              key={i}
              style={pinStretchHeight ? [styles.pinCell, { width: pinWidth }] : { width: pinWidth, minWidth: 0 }}
            >
              <TemplatePinCardPlaceholder width="100%" stretchHeight={pinStretchHeight} />
            </View>
          ))}
        </View>
      );
    } else if (sortedCatalogTemplates.length === 0) {
      catalogBody = (
        <View style={styles.empty}>
          <Icon name="clipboard-list" size={28} color={colors.text_color_tertiary} />
          <Text size="n" color="secondary" style={{ textAlign: 'center' }}>
            {t(searchQuery.trim() ? 'pages.tasks.noSearchResults' : 'pages.tasks.noTemplatesAvailable')}
          </Text>
          <Button
            text={t(searchQuery.trim() ? 'pages.tasks.clearSearch' : 'pages.tasks.createCustom')}
            variant="secondary"
            onPress={searchQuery.trim() ? () => setSearchQuery('') : handleCreateCustomTask}
          />
        </View>
      );
    } else if (viewMode === VIEW_MODES.LIST) {
      catalogBody = (
        <View style={styles.templateListStack}>
          {sortedCatalogTemplates.slice(0, templateLimit).map(tpl => (
            <TemplatePinListItem
              key={tpl.id || tpl.slug}
              template={tpl}
              onPress={handleTemplateOpenDetail}
              onSchedulePress={handleTemplateScheduleFromCatalog}
            />
          ))}
        </View>
      );
    } else {
      catalogBody = (
        <View style={[styles.cardGrid, !pinStretchHeight && styles.cardGridFlow]}>
          {sortedCatalogTemplates.slice(0, templateLimit).map(tpl => (
            <View
              key={tpl.id || tpl.slug}
              style={pinStretchHeight ? [styles.pinCell, { width: pinWidth }] : { width: pinWidth, minWidth: 0 }}
            >
              <TemplatePinCard
                template={tpl}
                width="100%"
                stretchHeight={pinStretchHeight}
                onPress={handleTemplateOpenDetail}
                onSchedulePress={handleTemplateScheduleFromCatalog}
              />
            </View>
          ))}
        </View>
      );
    }

    return (
      <View style={styles.block}>
        <View style={styles.blockHeader}>
          <Text weight="bold" size="m">{headerLabel}</Text>
          {!isCatalogSkeleton && !catalogFetchFailed ? (
            <Text size="xs" color="tertiary">{sortedCatalogTemplates.length}</Text>
          ) : null}
        </View>
        {catalogBody}
        {sortedCatalogTemplates.length > templateLimit && !isCatalogSkeleton && <View style={{ alignItems: 'center', paddingTop: 8 }}>
          <Button text={t('common.loadMore')} variant="secondary" onPress={() => setTemplateLimit(limit => limit + 12)} />
        </View>}
      </View>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      {!categorySlugProp && !browseMode && (
        <MetaHead
          title={t('pages.tasks.title')}
          description={t('pages.tasks.description')}
          noindex
        />
      )}

      <ScrollView
        ref={scrollRef}
        style={{ flex: 1 }}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        onScroll={(e) => {
          scrollYRef.current = e.nativeEvent.contentOffset.y;
        }}
        scrollEventThrottle={16}
        refreshControl={(
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={colors.text_color_secondary}
            colors={[colors.text_color_secondary]}
          />
        )}
      >
        <View style={styles.contentWrap}>
          <TasksIntro title={discoveryHeadline} subtitle={discoverySubtitle} onCreate={handleCreateCustomTask} />

          <View style={styles.section}>
            <TasksWorkspaceToolbar mode={workspaceMode} onModeChange={handleWorkspaceMode} hasUser={hasUser}
              searchQuery={searchQuery} onSearchChange={setSearchQuery} viewMode={viewMode} onViewModeChange={handleViewModeChange}>
              {workspaceMode === 'templates' && <>
                <CatalogCategoryDropdown categories={railCategories} activeCategorySlug={tasksActiveCategorySlug}
                  onChangeCategory={handleCategoryChange} topItems={tasksCategoryTopItems} showAllOption={false}
                  defaultTriggerLabel={t('pages.tasks.categoriesEyebrow')} allOptionLabel={scopes[0].label} />
                <CatalogSortDropdown value={catalogSort} onChange={setCatalogSort} label={t('pages.tasks.catalogSort')}
                  options={taskCatalogSortOptions} showLabel={false} />
              </>}
            </TasksWorkspaceToolbar>
            {hasUser && workspaceMode !== 'templates' && renderMyTasks()}
            {workspaceMode !== 'tasks' && renderCatalog()}
            {workspaceMode === 'tasks' && tasks.length > 0 && <Pressable accessibilityRole="button"
              accessibilityLabel={t('pages.tasks.browseTemplates')} onPress={() => handleWorkspaceMode('templates')}
              style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 20, borderRadius: 16, borderWidth: 1, borderColor: palette.border }}>
              <Icon name="sparkles" size={19} color={palette.icon} />
              <View style={{ flex: 1, gap: 4 }}><Text size="s" weight="semibold">{t('pages.tasks.browseTemplates')}</Text>
                <Text size="xs" color="secondary">{t('pages.tasks.templatesHint')}</Text></View>
              <Icon name="arrow-right" size={13} color={palette.icon} />
            </Pressable>}
              {workspaceMode === 'templates' && activeCategoryRecord && categoryMetaIntroText.length > 0 && (
                <View style={styles.categoryMetaIntro}>
                  <Text size="xs" weight="bold" color="tertiary" style={{ letterSpacing: 0.8 }}>
                    {t('pages.tasks.categoryMetaEyebrow').toUpperCase()}
                  </Text>
                  <Text size="n" color="secondary" style={{ lineHeight: 22 }}>
                    {categoryMetaIntroText}
                  </Text>
                </View>
              )}
              {workspaceMode === 'templates' && effectiveCategory && effectiveCategory !== 'featured' && categoryPageCopyBlocks.length > 0 && (
                <View style={styles.pageCopyWrap}>
                  <ToolPageCopy
                    blocks={categoryPageCopyBlocks}
                    palette={categoryPageCopyPalette}
                    isMD={isMD || isSM}
                  />
                </View>
              )}
          </View>
        </View>
      </ScrollView>
    </View>
  );
});

TasksPage.displayName = 'TasksPage';

function TasksRoute() {
  return <TasksPage />;
}

export default React.memo(TasksRoute);
